import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/common/ErrorBoundary';
import Sidebar from './components/Sidebar';
import PromptWizard from './components/PromptWizard';
import ManualPromptWizard from './components/ManualPromptWizard';
import ProjectNameModal from './components/ProjectNameModal';
import WorkspaceModal from './components/WorkspaceModal';
import WorkspaceSettings from './components/WorkspaceSettings';
import ProfileSettings from './components/ProfileSettings';
import { MonitoringDashboard } from './components/Monitoring';
import { ProjectsList, ProjectDetail, TestPromptPage, RefinementPage, RefinePromptPage, VersionControl } from './components/Projects';
import { Login, Register } from './components/Auth';
import { Home, About, Contact, Features, Docs } from './components/Public';
import { CompareModelsPage } from './components/ModelComparison';
import { SDKPage } from './components/SDK';
import { isAuthenticated, getUser, logout } from './services/auth';
import { getProjects, getAllRuns, getProject } from './services/api';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { ModalProvider } from './contexts/ModalContext';

const MainApp = () => {
  const navigate = useNavigate();
  const location = window.location.pathname;
  const { currentWorkspace, workspaces, loading: workspacesLoading } = useWorkspace();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetailLoading, setProjectDetailLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [testProject, setTestProject] = useState(null);
  const [refineProject, setRefineProject] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingProjectId, setStreamingProjectId] = useState(null);
  const [streamContent, setStreamContent] = useState({
    system: '',
    user: '',
    examples: ''
  });
  const [currentStreamLabel, setCurrentStreamLabel] = useState('system');

  useEffect(() => {
    if (isAuthenticated()) {
      setUser(getUser());
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const handlePopState = async () => {
      if (currentWorkspace && user) {
        const workspaceId = currentWorkspace.id || currentWorkspace._id;
        if (location.includes('/prompts') || location.includes('/prompt-details') || location.includes('/refine') || location.includes('/versions')) {
          const projectsResult = await getProjects(50, 0, workspaceId);
          if (projectsResult.success) {
            setProjects(projectsResult.data);
          }
        }
        if (location.includes('/test') || location.includes('/test-details')) {
          const runsResult = await getAllRuns(100, 0, workspaceId);
          if (runsResult.success) {
            setRuns(runsResult.data || []);
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentWorkspace, user, location]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user || !currentWorkspace) return;
      
      setProjectsLoading(true);
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      const result = await getProjects(50, 0, workspaceId);
      if (result.success) {
        setProjects(result.data);
      }
      setProjectsLoading(false);
    };

    if (user && currentWorkspace) {
      fetchProjects();
    }
  }, [user, currentWorkspace]);

  useEffect(() => {
    const fetchRuns = async () => {
      if (!user || !currentWorkspace) return;
      
      setRunsLoading(true);
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      const result = await getAllRuns(100, 0, workspaceId);
      if (result.success) {
        setRuns(result.data || []);
      }
      setRunsLoading(false);
    };

    if (user && currentWorkspace) {
      fetchRuns();
    }
  }, [user, currentWorkspace]);

  useEffect(() => {
    const loadProjectFromUrl = async () => {
      const projectMatch = location.match(/\/prompt-details\/([^/]+)/);
      if (projectMatch && projectMatch[1]) {
        const projectId = projectMatch[1];
        if (isStreaming && streamingProjectId === projectId) {
          return;
        }
        setProjectDetailLoading(true);
        const result = await getProject(projectId);
        if (result.success) {
          setSelectedProject(result.data);
        } else {
          toast.error(result.error || 'Failed to load prompt');
          navigate('/prompts');
        }
        setProjectDetailLoading(false);
      } else if (location === '/prompts') {
        if (!isStreaming) {
          setSelectedProject(null);
        }
        setProjectDetailLoading(false);
      }
    };
    
    if (user && (location.includes('/prompts') || location.includes('/prompt-details'))) {
      loadProjectFromUrl();
    }
  }, [location, user, navigate, isStreaming, streamingProjectId]);

  useEffect(() => {
    const loadRefineProjectFromUrl = async () => {
      const refineMatch = location.match(/\/refine-prompt\/([^/]+)/);
      if (refineMatch && refineMatch[1]) {
        const projectId = refineMatch[1];
        setProjectDetailLoading(true);
        const result = await getProject(projectId);
        if (result.success) {
          setRefineProject(result.data);
        } else {
          toast.error(result.error || 'Failed to load prompt for refinement');
          navigate('/refine');
        }
        setProjectDetailLoading(false);
      } else if (location === '/refine') {
        setRefineProject(null);
        setProjectDetailLoading(false);
      }
    };
    
    if (user && (location.includes('/refine') || location.includes('/refine-prompt'))) {
      loadRefineProjectFromUrl();
    }
  }, [location, user, navigate]);


  const handleLogout = useCallback(() => {
    logout();
    setUser(null);
    navigate('/login');
    setProjects([]);
  }, [navigate]);

  const handleTabChange = useCallback(async (tab) => {
    if (tab === 'generate') {
      setShowProjectModal(true);
    } else if (tab === 'prompts') {
      navigate('/prompts');
      setSelectedProject(null);
      if (currentWorkspace) {
        setProjectsLoading(true);
        const workspaceId = currentWorkspace.id || currentWorkspace._id;
        const result = await getProjects(50, 0, workspaceId);
        if (result.success) {
          setProjects(result.data);
        }
        setProjectsLoading(false);
      }
    } else if (tab === 'test') {
      navigate('/test');
      setTestProject(null);
      if (currentWorkspace) {
        setRunsLoading(true);
        const workspaceId = currentWorkspace.id || currentWorkspace._id;
        const result = await getAllRuns(100, 0, workspaceId);
        if (result.success) {
          setRuns(result.data || []);
        }
        setRunsLoading(false);
      }
    } else if (tab === 'compare') {
      navigate('/compare');
    } else if (tab === 'refine') {
      navigate('/refine');
      setRefineProject(null);
      if (currentWorkspace) {
        setProjectsLoading(true);
        const workspaceId = currentWorkspace.id || currentWorkspace._id;
        const result = await getProjects(50, 0, workspaceId);
        if (result.success) {
          setProjects(result.data);
        }
        setProjectsLoading(false);
      }
    } else if (tab === 'versions') {
      navigate('/versions');
      if (currentWorkspace) {
        setProjectsLoading(true);
        const workspaceId = currentWorkspace.id || currentWorkspace._id;
        const result = await getProjects(50, 0, workspaceId);
        if (result.success) {
          setProjects(result.data);
        }
        setProjectsLoading(false);
      }
    } else if (tab === 'monitoring') {
      navigate('/analytics');
    } else if (tab === 'sdk') {
      navigate('/sdk');
    } else if (tab === 'workspace-settings') {
      navigate('/workspace-settings');
    } else if (tab === 'profile-settings') {
      navigate('/profile-settings');
    }
  }, [navigate, currentWorkspace]);

  const handleProjectCreate = useCallback((projectName, projectId, method) => {
    setCurrentProjectName(projectName);
    setCurrentProjectId(projectId);
    setShowProjectModal(false);
    
    if (method === 'manual') {
      navigate('/new-project/manual');
    } else {
      navigate('/new-project');
    }
  }, [navigate]);

  const handleGenerationComplete = useCallback(async (result) => {
    toast.success('Prompt generated successfully!');
    if (!currentWorkspace) return;
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const projectsResult = await getProjects(50, 0, workspaceId);
    if (projectsResult.success) {
      setProjects(projectsResult.data);
      const newProject = projectsResult.data.find(p => p.id === result.id);
      if (newProject) {
        setSelectedProject(newProject);
      }
    }
    navigate('/prompts');
    setCurrentProjectName(null);
  }, [navigate, currentWorkspace]);

  const handleStreamProjectId = useCallback((projectId) => {
    setStreamingProjectId(projectId);
    setIsStreaming(true);
    setStreamContent({ system: '', user: '', examples: '' });
    navigate(`/prompt-details/${projectId}`);
    setCurrentProjectName(null);
    setSelectedProject({
      id: projectId,
      project_name: currentProjectName,
      status: 'generating'
    });
  }, [currentProjectName, navigate]);

  const handleStreamChunk = useCallback((data) => {
    if (data.type === 'start') {
      setCurrentStreamLabel(data.label);
    } else if (data.type === 'chunk') {
      setStreamContent(prev => ({
        ...prev,
        [data.label]: prev[data.label] + data.content
      }));
    }
  }, []);

  const handleStreamComplete = useCallback(async (projectId) => {
    setIsStreaming(false);
    setStreamingProjectId(null);
    toast.success('Prompt generated successfully!');
    if (!currentWorkspace) return;
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await getProjects(50, 0, workspaceId);
    if (result.success) {
      setProjects(result.data);
    }
    const projectResult = await getProject(projectId);
    if (projectResult.success) {
      setSelectedProject(projectResult.data);
      navigate(`/prompt-details/${projectId}`);
    }
  }, [currentWorkspace, navigate]);

  const handleStreamError = useCallback((error) => {
    setIsStreaming(false);
    setStreamingProjectId(null);
    toast.error(`Generation failed: ${error}`);
  }, []);

  const handleProjectCancel = useCallback(() => {
    setShowProjectModal(false);
  }, []);

  const handleSelectProject = useCallback((project) => {
    navigate(`/prompt-details/${project.id}`);
  }, [navigate]);

  const handleBackFromDetail = useCallback(async () => {
    setSelectedProject(null);
    navigate('/prompts');
    if (currentWorkspace) {
      setProjectsLoading(true);
      const workspaceId = currentWorkspace.id || currentWorkspace._id;
      const result = await getProjects(50, 0, workspaceId);
      if (result.success) {
        setProjects(result.data);
      }
      setProjectsLoading(false);
    }
  }, [navigate, currentWorkspace]);

  const handleDeleteProject = useCallback(async (projectId) => {
    if (!currentWorkspace) return;
    const workspaceId = currentWorkspace.id || currentWorkspace._id;
    const result = await getProjects(50, 0, workspaceId);
    if (result.success) {
      setProjects(result.data);
    }
  }, [currentWorkspace]);

  const handleTestProject = useCallback(async (project) => {
    // Fetch full project data including input_variables for test configuration
    const result = await getProject(project.id);
    if (result.success) {
      setTestProject(result.data);
      navigate('/test');
    } else {
      toast.error('Failed to load prompt details');
    }
  }, [navigate]);
  
  const handleRefineProject = useCallback((project) => {
    navigate(`/refine-prompt/${project.id}`);
  }, [navigate]);

  const handleSelectTestProject = useCallback(async (project) => {
    // Fetch full project data including input_variables for test configuration
    const result = await getProject(project.id);
    if (result.success) {
      setTestProject(result.data);
    } else {
      toast.error('Failed to load prompt details');
    }
  }, []);

  const handleSelectRefineProject = useCallback(async (project) => {
    // Fetch full project data including input_variables for refinement
    const result = await getProject(project.id);
    if (result.success) {
      setRefineProject(result.data);
    } else {
      toast.error('Failed to load prompt details');
    }
  }, []);

  // Show loading while initial auth check is happening
  // Also show loading if authenticated but user object isn't loaded yet
  if (loading || (isAuthenticated() && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-bg-primary via-bg-secondary to-bg-tertiary">
        <div className="w-12 h-12 border-4 rounded-full animate-spin border-primary-500/20 border-t-primary-500 shadow-glow"></div>
      </div>
    );
  }

  // Show loading while workspaces are being fetched (for authenticated users)
  if (user && workspacesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-bg-primary via-bg-secondary to-bg-tertiary">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 rounded-full animate-spin border-primary-500/20 border-t-primary-500 shadow-glow"></div>
          <p className="text-text-secondary text-sm">Loading your workspaces...</p>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    if (user && !workspacesLoading && workspaces.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 lg:gap-6 p-6 lg:p-10 text-center relative">
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_50%,rgba(50,205,50,0.05)_0%,transparent_50%)]" />
          <div className="flex flex-col items-center gap-3 lg:gap-4 relative">
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center bg-success-50 border border-primary-500/20">
              <svg className="w-6 h-6 lg:w-8 lg:h-8 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-2 text-text-primary">No Workspaces Available</h2>
              <p className="text-sm lg:text-base max-w-md text-text-tertiary">
                You don't have any workspaces yet. Create a new workspace to get started with your prompts.
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowWorkspaceModal(true)}
            className="px-4 lg:px-6 py-2 lg:py-2.5 text-sm lg:text-base font-medium rounded-lg transition-all duration-200 flex items-center gap-2 bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm hover:from-primary-500 hover:to-green-400 hover:-translate-y-0.5 hover:shadow-glow"
          >
            <svg className="w-4 h-4 lg:w-5 lg:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Workspace
          </button>
        </div>
      );
    }

    if (location === '/new-project/manual' && currentProjectName && currentProjectId) {
      const workspaceId = currentWorkspace ? (currentWorkspace.id || currentWorkspace._id) : null;
      return (
          <ManualPromptWizard
          projectName={currentProjectName}
          projectId={currentProjectId}
          workspaceId={workspaceId}
          onComplete={async (projectId) => {
            if (currentWorkspace) {
              const workspaceId = currentWorkspace.id || currentWorkspace._id;
              const result = await getProjects(50, 0, workspaceId);
              if (result.success) {
                setProjects(result.data);
              }
            }
            navigate(`/prompt-details/${projectId}`);
          }}
        />
      );
    }

    if (location === '/new-project' && currentProjectName && currentProjectId) {
      const workspaceId = currentWorkspace ? (currentWorkspace.id || currentWorkspace._id) : null;
      return (
        <PromptWizard
          projectName={currentProjectName}
          projectId={currentProjectId}
          workspaceId={workspaceId}
          onComplete={handleGenerationComplete}
          onStreamProjectId={handleStreamProjectId}
          onStreamChunk={handleStreamChunk}
          onStreamComplete={handleStreamComplete}
          onStreamError={handleStreamError}
        />
      );
    }

    if (location.includes('/workspace-settings')) {
      return <WorkspaceSettings onBack={() => navigate('/prompts')} />;
    }

    if (location.includes('/profile-settings')) {
      return <ProfileSettings user={user} onBack={() => navigate('/prompts')} onUserUpdate={setUser} />;
    }

    if (location.includes('/analytics')) {
      return <MonitoringDashboard />;
    }

    if (location.includes('/sdk')) {
      return <SDKPage />;
    }

    if (location.includes('/compare-details/')) {
      return <CompareModelsPage />;
    }

    if (location.includes('/compare')) {
      return <CompareModelsPage />;
    }

    if (location.includes('/versions') || location.includes('/version-details')) {
      return <VersionControl projects={projects} />;
    }

    if (location.includes('/prompt-details')) {
      // Show loading state while fetching project data
      if (projectDetailLoading || !selectedProject) {
        return (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 lg:gap-4 p-6 lg:p-10 text-center">
            <div className="w-8 h-8 lg:w-10 lg:h-10 border-2 border-border-primary border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-text-secondary text-sm lg:text-base mt-2 lg:mt-4">Loading prompt details...</p>
          </div>
        );
      }
      return (
        <ProjectDetail
          project={selectedProject}
          onBack={handleBackFromDetail}
          onDelete={handleDeleteProject}
          onTestProject={handleTestProject}
          onRefineProject={handleRefineProject}
          isStreaming={isStreaming && streamingProjectId === selectedProject.id}
          streamContent={streamContent}
          currentStreamLabel={currentStreamLabel}
        />
      );
    }

    if (location.includes('/prompts')) {
      return (
        <ProjectsList
          projects={projects}
          selectedProjectId={selectedProject?.id}
          onSelectProject={handleSelectProject}
          onDelete={handleDeleteProject}
          loading={projectsLoading}
          onNewProject={() => setShowProjectModal(true)}
        />
      );
    }

    if (location.includes('/refine-prompt') || location === '/refine') {
      if (projectDetailLoading && location.includes('/refine-prompt')) {
        return (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 lg:gap-4 p-6 lg:p-10 text-center">
            <div className="w-8 h-8 lg:w-10 lg:h-10 border-2 border-border-primary border-t-primary-500 rounded-full animate-spin"></div>
            <p className="text-text-secondary text-sm lg:text-base mt-2 lg:mt-4">Loading prompt for refinement...</p>
          </div>
        );
      }
      if (refineProject) {
      return (
        <RefinementPage
          project={refineProject}
          onBack={() => {
            navigate('/refine');
            setRefineProject(null);
            setSelectedProject(refineProject);
          }}
          />
        );
      }
      return (
        <RefinePromptPage
          project={null}
          projects={projects}
          onSelectProject={handleSelectRefineProject}
          onBack={() => navigate('/prompts')}
        />
      );
    }

    if (location.includes('/test') || location.includes('/test-details')) {
      return (
        <TestPromptPage
          project={testProject}
          projects={projects}
          runs={runs}
          runsLoading={runsLoading}
          onRunsUpdate={setRuns}
          onSelectProject={handleSelectTestProject}
          onBack={() => {
            navigate('/prompts');
            if (testProject) {
              setSelectedProject(testProject);
            }
          }}
        />
      );
    }

    return (
      <div className="flex-1 flex items-center justify-center p-10 relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_30%,rgba(50,205,50,0.06)_0%,transparent_50%)]" />
        
        <div className="text-center max-w-lg p-8 rounded-xl relative bg-bg-secondary/80 border border-primary-500/15 shadow-[0_20px_40px_rgba(3,6,4,0.4),0_0_40px_rgba(50,205,50,0.08)]">
          <img 
            src="/logo.png"
            alt="ForgeVault Logo" 
            className="w-20 h-20 mx-auto mb-6 object-contain"
          />
          <h2 className="text-2xl font-semibold mb-3 text-text-primary">Welcome to ForgeVault</h2>
          <p className="text-base mb-6 leading-relaxed text-text-secondary">Start crafting powerful AI prompts by creating your first prompt. Build, test, and refine your prompt templates all in one place.</p>
          <button 
            className="px-6 py-3 text-base font-semibold rounded-lg cursor-pointer transition-all inline-flex items-center gap-2 bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow-sm hover:from-primary-500 hover:to-green-400 hover:-translate-y-0.5 hover:shadow-glow"
            onClick={() => setShowProjectModal(true)}
          >
            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create Your First Prompt
          </button>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <div className="h-screen flex flex-col overflow-hidden bg-bg-primary">
        <div className="flex-1 flex min-h-0 overflow-hidden h-screen gap-1">
          <Sidebar
            activeTab={
              location.includes('/new-project') ? 'generate' : 
              (location.includes('/test') || location.includes('/test-details')) ? 'test' : 
              (location.includes('/compare') || location.includes('/compare-details')) ? 'compare' :
              location.includes('/refine') ? 'refine' :
              (location.includes('/versions') || location.includes('/version-details')) ? 'versions' :
              location.includes('/analytics') ? 'monitoring' :
              location.includes('/sdk') ? 'sdk' :
              location.includes('/workspace-settings') ? 'workspace-settings' :
              location.includes('/profile-settings') ? 'profile-settings' :
              location.includes('/prompts') || location.includes('/prompt-details') ? 'prompts' :
              'prompts'
            }
            onTabChange={handleTabChange}
            user={user}
            onLogout={handleLogout}
            onCreateWorkspace={() => setShowWorkspaceModal(true)}
          />
          <main className="flex-1 flex flex-col overflow-hidden min-h-0 w-full lg:w-auto bg-bg-primary border-l border-white/10">
            {renderContent()}
          </main>
        </div>
      </div>
      
      {showProjectModal && (
        <ProjectNameModal
          onSubmit={handleProjectCreate}
          onCancel={handleProjectCancel}
        />
      )}
      
      {showWorkspaceModal && (
        <WorkspaceModal
          isOpen={showWorkspaceModal}
          onClose={() => setShowWorkspaceModal(false)}
        />
      )}
      
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        theme="dark"
      />
    </ErrorBoundary>
  );
};

// Public page wrapper - redirects to prompts if authenticated
const PublicPageWrapper = ({ children }) => {
  // If already authenticated, redirect to prompts
  if (isAuthenticated()) {
    return <Navigate to="/prompts" replace />;
  }
  
  return children;
};

PublicPageWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

// Auth wrapper component - checks URL to determine login vs signup
const AuthWrapper = ({ isSignup = false }) => {
  const navigate = useNavigate();
  
  const handleLogin = (userData) => {
    navigate('/prompts');
  };
  
  const handleRegister = (userData) => {
    navigate('/prompts');
  };
  
  // If already authenticated, redirect to prompts
  if (isAuthenticated()) {
    return <Navigate to="/prompts" replace />;
  }
  
  if (isSignup) {
    return (
      <Register 
        onRegister={handleRegister} 
        onSwitchToLogin={() => navigate('/login')} 
      />
    );
  }
  
  return (
    <Login 
      onLogin={handleLogin} 
      onSwitchToRegister={() => navigate('/signup')} 
    />
  );
};

AuthWrapper.propTypes = {
  isSignup: PropTypes.bool,
};

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const App = () => {
  return (
    <BrowserRouter>
      <WorkspaceProvider>
        <ModalProvider>
          <Routes>
            {/* Public Routes - Marketing/Landing Pages */}
            <Route path="/" element={<PublicPageWrapper><Home /></PublicPageWrapper>} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Public Documentation */}
            <Route path="/docs" element={<Docs />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<AuthWrapper isSignup={false} />} />
            <Route path="/signup" element={<AuthWrapper isSignup={true} />} />
            
            {/* Main App Routes (with sidebar, protected) */}
            <Route path="/prompts" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/prompt-details/:projectId" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/new-project" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/new-project/manual" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/versions" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/version-details/:id" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/version-details/:id/compare" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/test" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/test/new" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/test-details/:runId" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/refine" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/refine-prompt/:projectId" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/compare" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/compare/new" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/compare-details/:comparisonId" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/sdk" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/workspace-settings" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
            <Route path="/profile-settings" element={<ProtectedRoute><MainApp /></ProtectedRoute>} />
          </Routes>
        </ModalProvider>
      </WorkspaceProvider>
    </BrowserRouter>
  );
};

export default App;
